import { Label, RequiredAsterisk } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { LayoutSettingSelectedEventData } from '@databases/analytics/src/types';
import FieldPicker from '@databases/field-picker/FieldPicker';
import { ErrorMessage, HelperMessage } from '@databases/form/styled';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseField } from '@databases/types/database';
import { getDatabaseFields } from '@databases/utils/database/utils';
import {
  ViewChangeTypes,
  isValidDateField,
  getDateField,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';

export interface GroupFieldSettingProps {
  contentId: string;
  source: LayoutSettingSelectedEventData['source'];
}

const DateFieldSetting: FunctionComponent<GroupFieldSettingProps> = ({
  contentId,
  source,
}) => {
  const { formatMessage } = useIntl();
  const client = useAnalytics();
  const { view, setViewChange } = useView();
  const layout = view.settings.layout;
  const [database, possibleFields] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    return [
      database,
      getDatabaseFields(database).filter((field) => isValidDateField(field)),
    ] as const;
  });

  const resolvedValue = useMemo(() => getDateField(database, layout), [database, layout]);
  const handleChange = useCallback(
    async (field: DatabaseField | undefined) => {
      setViewChange({ type: ViewChangeTypes.LAYOUT_DATE_FIELD_ID, value: field?.id });
      await client.sendLayoutSettingSelectedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source,
        layoutSetting: 'dateField',
        value: field ? field.id : undefined,
      });
    },
    [client, contentId, setViewChange, source]
  );

  return (
    <Box>
      <Label htmlFor="date-field-setting">
        {formatMessage(messages['view-settings.layout.date-field.label'])}
        <RequiredAsterisk />
      </Label>
      <FieldPicker
        fields={possibleFields}
        value={resolvedValue}
        onChange={handleChange}
        isClearable={false}
        isDisabled={!possibleFields.length}
        isInvalid={!possibleFields.length}
        placeholder={formatMessage(
          messages['view-settings.layout.date-field.placeholder']
        )}
      />
      {!possibleFields.length ? (
        <ErrorMessage>
          {formatMessage(messages['view-settings.layout.date-field.error-message'])}
        </ErrorMessage>
      ) : (
        <HelperMessage>
          {formatMessage(messages['view-settings.layout.date-field.helper-message'])}
        </HelperMessage>
      )}
    </Box>
  );
};

export default DateFieldSetting;
